import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { SeoService } from '../../services/seoService';
import { aiClientService } from '../../services/aiService';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import { getSiteDisplayAddress } from '../../utils/security';
import {
  Search,
  Globe,
  Share2,
  FileCode,
  CheckCircle2,
  Copy,
  Check,
  Save,
  Sparkles,
  Loader2,
} from 'lucide-react';

export const SeoManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite, updateSiteData, showToast } = useSite();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [isGeneratingAiSeo, setIsGeneratingAiSeo] = useState(false);

  const [metaTitle, setMetaTitle] = useState(
    activeSite?.settings?.metaTitle || (activeSite?.name ? `${activeSite.name} — Resmi Web Sitesi` : '')
  );
  const [metaDescription, setMetaDescription] = useState(
    activeSite?.settings?.metaDescription ||
      (activeSite?.name ? `${activeSite.name} ile profesyonel hizmetler ve sektörün en iyi çözümleri.` : '')
  );
  const [metaKeywords, setMetaKeywords] = useState(
    activeSite?.settings?.metaKeywords || 'kurumsal, web, yat kiralama, ege, mavi tur'
  );

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="SEO & Arama Motoru Optimizasyonu İçin Web Sitesi Gereklidir"
        description="Google indeksleme, meta başlıkları, sitemap.xml ve robots.txt ayarlarını düzenlemek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const domain = getSiteDisplayAddress(activeSite);
  const sitemapXml = SeoService.generateSitemapXml(activeSite);
  const robotsTxt = SeoService.generateRobotsTxt(activeSite);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleAiSeoGenerate = async () => {
    setIsGeneratingAiSeo(true);
    try {
      const seo = await aiClientService.generateSEO(
        activeSite.name,
        activeSite.industry,
        `${activeSite.name} web sitesi ana sayfası ve hizmetleri`
      );
      if (seo.title) setMetaTitle(seo.title);
      if (seo.description) setMetaDescription(seo.description);
      if (seo.keywords && seo.keywords.length > 0) setMetaKeywords(seo.keywords.join(', '));
      if (showToast) showToast('AI SEO önerileri oluşturuldu.', 'success');
    } catch (err) {
      console.error('AI SEO generation error:', err);
    } finally {
      setIsGeneratingAiSeo(false);
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateSiteData({
      settings: {
        ...(activeSite.settings || {}),
        metaTitle,
        metaDescription,
        metaKeywords,
      },
    });
    if (showToast) {
      showToast('SEO ayarları başarıyla kaydedildi.', 'success');
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-pink-600">
            <Search className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Arama Motoru Optimizasyonu</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            SEO, Meta ve Sitemap Yönetimi
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Google, Yandex ve sosyal medya platformlarında sitenizin nasıl görüneceğini optimize edin.
          </p>
        </div>

        <button
          type="button"
          onClick={handleAiSeoGenerate}
          disabled={isGeneratingAiSeo}
          className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-pink-600 hover:from-indigo-500 hover:to-pink-500 text-white text-xs font-bold shadow-lg shadow-indigo-500/20 transition flex items-center gap-2 self-start sm:self-auto disabled:opacity-50"
        >
          {isGeneratingAiSeo ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>AI Optimize Ediyor...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              <span>AI ile SEO Önerisi Al</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Col: SEO Meta Inputs */}
        <form onSubmit={handleSave} className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-5">
          <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-600" />
            Genel Meta Etiketleri
          </h3>

          <div>
            <div className="flex justify-between text-xs font-semibold text-slate-700 mb-1">
              <label>SEO Başlığı (Meta Title)</label>
              <span className="text-[11px] text-slate-400">{metaTitle.length}/60 karakter</span>
            </div>
            <input
              type="text"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs font-semibold text-slate-700 mb-1">
              <label>SEO Açıklaması (Meta Description)</label>
              <span className="text-[11px] text-slate-400">{metaDescription.length}/160 karakter</span>
            </div>
            <textarea
              rows={3}
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Anahtar Kelimeler (Virgülle Ayırın)</label>
            <input
              type="text"
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="w-full py-2.5 bg-pink-600 hover:bg-pink-500 text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 shadow"
            >
              <Save className="w-4 h-4" />
              SEO Ayarlarını Kaydet
            </button>
          </div>
        </form>

        {/* Right Col: Live Google & Social Preview */}
        <div className="space-y-6">
          {/* Google Search Snippet Preview */}
          <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider">
              <span>Google Arama Sonucu Önizlemesi</span>
              <span className="text-emerald-600 font-semibold text-[10px]">Mobil / Masaüstü</span>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-1">
              <div className="text-[11px] text-slate-600 flex items-center gap-1 font-mono truncate">
                https://{domain}
              </div>
              <div className="text-blue-700 font-semibold text-base hover:underline cursor-pointer line-clamp-1">
                {metaTitle || 'Başlık'}
              </div>
              <div className="text-slate-600 text-xs line-clamp-2 leading-relaxed">
                {metaDescription || 'Açıklama'}
              </div>
            </div>
          </div>

          {/* Social Share Card Preview */}
          <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider">
              <span>Sosyal Medya Kart Önizlemesi (OG Tag)</span>
              <Share2 className="w-4 h-4 text-slate-400" />
            </div>

            <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
              <div className="h-32 bg-slate-800 flex items-center justify-center text-slate-500 text-xs">
                {activeSite.settings?.logoUrl ? (
                  <img src={activeSite.settings?.logoUrl} alt="Logo" className="h-12 object-contain" />
                ) : (
                  <span className="font-bold text-slate-300">{activeSite.name} Sosyal Kapak</span>
                )}
              </div>
              <div className="p-3">
                <div className="text-[10px] uppercase font-mono text-slate-400">{domain}</div>
                <div className="font-bold text-xs text-slate-900 mt-0.5">{metaTitle}</div>
                <div className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{metaDescription}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auto Generated Sitemap.xml & Robots.txt Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sitemap.xml */}
        <div className="p-6 bg-slate-900 text-white rounded-2xl border border-slate-800 space-y-3 shadow-xl">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-xs flex items-center gap-2 text-emerald-400">
              <FileCode className="w-4 h-4" /> Otomatik Sitemap.xml
            </h4>
            <button
              onClick={() => handleCopy(sitemapXml, 'sitemap')}
              className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] flex items-center gap-1"
            >
              {copiedKey === 'sitemap' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
              Kopyala
            </button>
          </div>
          <pre className="p-3 bg-slate-950 rounded-xl text-[10px] font-mono text-slate-300 overflow-x-auto max-h-36">
            {sitemapXml}
          </pre>
        </div>

        {/* Robots.txt */}
        <div className="p-6 bg-slate-900 text-white rounded-2xl border border-slate-800 space-y-3 shadow-xl">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-xs flex items-center gap-2 text-blue-400">
              <FileCode className="w-4 h-4" /> Otomatik Robots.txt
            </h4>
            <button
              onClick={() => handleCopy(robotsTxt, 'robots')}
              className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] flex items-center gap-1"
            >
              {copiedKey === 'robots' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
              Kopyala
            </button>
          </div>
          <pre className="p-3 bg-slate-950 rounded-xl text-[10px] font-mono text-slate-300 overflow-x-auto max-h-36">
            {robotsTxt}
          </pre>
        </div>
      </div>
    </div>
  );
};
